/** @format */

import {
  CardReference,
  Resource,
  UserResource,
  UuidString,
} from "../resources/types";
import {
  checkSublesson,
  getUserResourceSubLessonCards,
} from "../userResources/userResourceFunctions";
import { produce } from "immer";
import { store } from "../../app/store";
import { userCardsApi } from "../../app/services/userResource/userCardsApi";
import { userLessonApi } from "../../app/services/userResource/userLessonApi";
import { cardsApi } from "../../app/services/resource/cardsApi";
import {
  isValidUuid,
  saveCard as saveResourceCard,
} from "../resources/ResourceFunctions";
import { saveCard as saveUserResourceCard } from "../userResources/userResourceFunctions";
import { Locale } from "../i18n/init";
import { resourcesApi } from "../../app/services/resource/resourcesApi";
import { userResourceApi } from "../../app/services/userResource/userResourcesApi";
import { getResourceCode } from "../../utils/GetResourceCode";

/** Remove card and card reference from a UserResource */
const deleteUserCard = async (
  lessonIndex: number,
  sublessonIndex: number,
  resource: UserResource,
  itemId: string,
) => {
  const dispatch = store.dispatch;
  const locale = resource.Locale;
  const copy = resource.Copy;

  const subLesson =
    resource.Object.lessons[lessonIndex].subLessons[sublessonIndex];
  const selectedCardIndex = subLesson.cards.findIndex(
    (item) => item.uuid === itemId,
  );
  const card = subLesson.cards[selectedCardIndex];

  // Check if card is a card created by a user or not
  if (card?.originalUUID !== undefined) {
    // Card is a user Card so delete the card
    const subLessonUuid = subLesson.uuid;
    try {
      await dispatch(
        userCardsApi.endpoints.deleteUserResourceCard.initiate({
          code: resource.Code,
          locale,
          subLessonUuid,
          cardUuid: itemId,
          copy,
        }),
      );
    } catch {
      console.log("Failed to delete card");
    }
  }

  // Remove the card's reference in the User Resource
  try {
    let newCards: CardReference[] = [
      ...resource.Object.lessons[lessonIndex].subLessons[sublessonIndex].cards,
    ];

    newCards.splice(selectedCardIndex, 1);

    let newResource = produce(resource, (draft) => {
      let sublesson =
        draft.Object.lessons[lessonIndex].subLessons[sublessonIndex];
      draft.Object.lessons[lessonIndex].subLessons[sublessonIndex] =
        resource.PK.includes("USER_BL#")
          ? checkSublesson({ ...sublesson, cards: newCards })
          : { ...sublesson, cards: newCards };
    });

    if (selectedCardIndex >= 0) {
      await dispatch(
        userLessonApi.endpoints.saveUserLesson.initiate({
          diff: { Object: { lessons: newResource.Object.lessons } },
          primaryKey: { PK: resource.PK, SK: resource.SK },
          userEmailHash: resource.UserEmailHash,
          locale: locale,
          copy: resource.Copy,
        }),
      );
    }
  } catch {
    console.log("Failed to remove card reference");
  }
};

/** Remove card reference from a Resource */
const deleteResourceCard = async (
  lessonIndex: number,
  sublessonIndex: number,
  resource: Resource,
  itemId: string,
) => {
  const dispatch = store.dispatch;

  try {
    if (isValidUuid(itemId)) {
      const code = getResourceCode(resource);
      const subLessonUuid: UuidString = resource.Object.lessons[lessonIndex]
        .subLessons[sublessonIndex].uuid as UuidString;

      if (subLessonUuid) {
        await dispatch(
          cardsApi.endpoints.deleteResourceCard.initiate({
            code,
            subLessonUuid,
            cardUuid: itemId as UuidString,
          }),
        );
      } else {
        console.warn("Unable to find sublesson");
      }
    }
  } catch {
    console.log("Failed to delete card");
  }
};

export const deleteCard = async (
  resource: Resource | UserResource,
  lessonIndex: number,
  sublessonIndex: number,
  itemId: string,
) => {
  if (resource.PK.includes("USER_BL#")) {
    await deleteUserCard(
      lessonIndex,
      sublessonIndex,
      resource as UserResource,
      itemId,
    );
  } else {
    await deleteResourceCard(
      lessonIndex,
      sublessonIndex,
      resource as Resource,
      itemId,
    );
  }
};

type Action = "hint" | "extension" | "answer" | "downloadFiles";

export const deleteTab = async (
  resource: Resource | UserResource,
  lessonIndex: number,
  sublessonIndex: number,
  itemId: string,
  action: string,
  locale: Locale,
) => {
  const dispatch = store.dispatch;

  let _action = action === "download" ? "downloadFiles" : (action as Action);
  const resourceId = getResourceCode(resource);
  const cardUuids = [itemId];
  let sublesson =
    resource.Object.lessons[lessonIndex].subLessons[sublessonIndex];
  const subLessonUuid = sublesson.uuid;

  if (resource.PK.includes("USER_BL#")) {
    let card = await getUserResourceSubLessonCards({
      locale,
      resource: resource.Object,
      code: getResourceCode(resource),
      lessonIndex,
      subLessonUuid,
      copy: (resource as UserResource).Copy,
    });
    card = card?.filter((item) => item.UUID === itemId);

    if (card && card[0]) {
      let _card = produce(card[0], (draft) => {
        delete draft.Object[_action];
      });

      await saveUserResourceCard(
        _card,
        locale,
        resource as UserResource,
        lessonIndex,
        sublessonIndex,
      );
    }
  } else {
    const { data: card } = await dispatch(
      cardsApi.endpoints.getResourceSubLessonCards.initiate({
        resourceId,
        subLessonUuid,
        cardUuids,
      }),
    );

    if (card && card[0]) {
      let _card = produce(card[0], (draft) => {
        if (_action !== "downloadFiles") {
          draft.Object[_action] = {
            ...draft.Object[_action],
            [locale]: undefined,
          };
        } else {
          const files = draft.Object[_action];
          if (files) {
            if (Array.isArray(files)) {
              draft.Object[_action] = undefined;
            } else {
              draft.Object[_action] = { ...files, [locale]: [] };
            }
          }
        }
      });

      await saveResourceCard(
        _card,
        resource as Resource,
        lessonIndex,
        sublessonIndex,
      );
    }
  }
};

export const deleteLesson = async (
  resource: UserResource | Resource,
  itemId: string,
  locale: Locale,
) => {
  const dispatch = store.dispatch;

  const _resource = produce(resource, (draft) => {
    const index = draft.Object.lessons.findIndex(
      (lesson) => lesson.uuid === itemId,
    );

    if (index >= 0) draft.Object.lessons.splice(index, 1);
  });

  try {
    if (
      resource.PK.includes("USER_BL#") &&
      (resource as UserResource).Copy !== undefined
    ) {
      await dispatch(
        userResourceApi.endpoints.saveUserResource.initiate({
          // userResourceItem: _resource as UserResource,
          diff: {
            Object: {
              lessons: _resource.Object.lessons,
            },
          },
          primaryKey: { PK: resource.PK, SK: resource.SK },
          userEmailHash: (resource as UserResource).UserEmailHash,
          locale: locale,
          copy: (resource as UserResource).Copy,
        }),
      );
    } else {
      await dispatch(
        resourcesApi.endpoints.updateResource.initiate({
          primaryKey: {
            PK: resource.PK,
            SK: resource.SK,
          },
          diff: {
            Object: {
              lessons: _resource.Object.lessons,
            },
          },
        }),
      );
    }
  } catch {
    console.log("Failed to delete lesson");
  }
};
